import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import type { ChallengeReduxState } from '../../types/models/challenges';

import {
  useUpdateChallengesConsentMutation,
  useRemoveChallengesConsentMutation,
} from '../challengesApiSlice';
import { setChallengeAfterToogleConsent } from '../challengesSlice';

interface UseChallengeConsentProps {
  challenge: ChallengeReduxState;
  setErrorText: (text: string) => void;
  toggleActivePage: (tabid: number) => void;
}

export function useChallengeConsent({
  challenge,
  setErrorText,
  toggleActivePage,
}: UseChallengeConsentProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showSubmitTrackerModal, setShowSubmitTrackerModal] = useState(false);
  const [updateChallengesConsent] = useUpdateChallengesConsentMutation();
  const [removeChallengesConsent] = useRemoveChallengesConsentMutation();

  const handleCloseModal = () => {
    setShowConsentModal(false);
    setShowSubmitTrackerModal(false);
  };

  const handleToggleConsentSwitch = async () => {
    if (!challenge?.userGaveConsent && challenge?.userProgressOverall > 0) {
      setShowConsentModal(true);
    } else if (!challenge?.userGaveConsent && challenge?.userProgressOverall === 0) {
      setShowSubmitTrackerModal(true);
    } else {
      try {
        const result = await removeChallengesConsent(challenge?._id);
        if ('data' in result && result.data) {
          dispatch(setChallengeAfterToogleConsent({ challenge: result.data }));
        }
        if ('error' in result) {
          setErrorText(t('challenge.error.leaderboard.remove'));
        }
      } catch {
        setErrorText(t('challenge.error.leaderboard.remove'));
      }
    }
  };

  const handleCloseConsentModal = async () => {
    setShowConsentModal(false);
    try {
      const result = await updateChallengesConsent(challenge?._id);
      if ('data' in result && result.data) {
        dispatch(setChallengeAfterToogleConsent({ challenge: result.data }));
      }
      if ('error' in result) {
        setErrorText(t('challenge.error.leaderboard.add'));
      }
    } catch {
      setErrorText(t('challenge.error.leaderboard.add'));
    }
  };

  const handleCloseSubmitTrackerModal = () => {
    setShowSubmitTrackerModal(false);
    toggleActivePage(2);
  };

  const consentModalContents = {
    title: t('challenge.modal.consent.title'),
    textArr: [{ text: t('challenge.modal.consent.description') }],
    buttons: [
      {
        text: t('challenge.modal.button.cancel'),
        color: 'dark',
        onClick: () => setShowConsentModal(false),
      },
      {
        text: t('challenge.modal.consent.button.confirm'),
        color: 'pink',
        onClick: handleCloseConsentModal,
      },
    ],
  };

  const submitTrackerModalContents = {
    title: t('challenge.modal.tracker.title'),
    textArr: [{ text: t('challenge.modal.tracker.description') }],
    buttons: [
      {
        text: t('challenge.modal.button.cancel'),
        color: 'dark',
        onClick: () => setShowSubmitTrackerModal(false),
      },
      {
        text: t('challenge.modal.tracker.button.open'),
        color: 'pink',
        onClick: handleCloseSubmitTrackerModal,
      },
    ],
  };

  return {
    showConsentModal,
    showSubmitTrackerModal,
    handleToggleConsentSwitch,
    handleCloseConsentModal,
    handleCloseModal,
    consentModalContents,
    submitTrackerModalContents,
  };
}
